import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { detailsVacApp, updateVacApp } from "../../actions/vaccineAppointmentActions";
import { useNavigate, useParams } from "react-router-dom";

const CyBocsRating = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const navigate = useNavigate();

  // Redux state
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const vaccineAppList = useSelector((state) => state.vaccineAppDetails);
  const { appointment: { appointment } = {} } = vaccineAppList || {};

  const [obsessionScores, setObsessionScores] = useState(Array(5).fill(0));
  const [compulsionScores, setCompulsionScores] = useState(Array(5).fill(0));

  // ✅ Prefill from appointment.cybcos if exists
  useEffect(() => {
    if (appointment?.cybcos) {
      setObsessionScores(appointment.cybcos.obsessionScores || Array(5).fill(0));
      setCompulsionScores(appointment.cybcos.compulsionScores || Array(5).fill(0));
    }
  }, [appointment]);

  const handleChange = (section, index, value) => {
    if (section === "obsession") {
      const updated = [...obsessionScores];
      updated[index] = value;
      setObsessionScores(updated);
    } else {
      const updated = [...compulsionScores];
      updated[index] = value;
      setCompulsionScores(updated);
    }
  };

  const subtotalObsessions = obsessionScores.reduce((a, b) => a + b, 0);
  const subtotalCompulsions = compulsionScores.reduce((a, b) => a + b, 0);
  const score = subtotalObsessions + subtotalCompulsions;

  const classify = () => {
    if (score <= 7) return "Subclinical";
    if (score <= 15) return "Mild";
    if (score <= 23) return "Moderate";
    if (score <= 31) return "Severe";
    return "Extreme";
  };

  const handleSubmit = () => {
    const formResult = {
      obsessionScores,
      compulsionScores,
      subtotalObsessions,
      subtotalCompulsions,
      score, // ✅ renamed from total
      classification: classify(),
    };

    console.log("CY-BOCS Submitted:", formResult);

    // Save score to localStorage for Psychodiagnostic form
    const localStorageKey = `psycho_tools_${id}`;
    const existingScores = JSON.parse(localStorage.getItem(localStorageKey) || '{}');
    existingScores['CYBCOS'] = score;
    localStorage.setItem(localStorageKey, JSON.stringify(existingScores));

    // ✅ Save in appointment
    if (appointment?._id) {
      dispatch(
        updateVacApp({
          _id: appointment._id,
          cybcos: formResult,
        })
      );
    }

    alert(
      `Form submitted!\nScore: ${score}\nClassification: ${classify()}`
    );
  };

  useEffect(() => {
    if (userInfo) {
      dispatch(detailsVacApp(id));
    } else {
      navigate("/signin");
    }
  }, [dispatch, userInfo, id, navigate]);

  const questions = {
    obsessions: [
      "Time occupied by obsessive thoughts",
      "Interference due to obsessive thoughts",
      "Distress associated with obsessive thoughts",
      "Resistance against obsessions",
      "Degree of control over obsessive thoughts",
    ],
    compulsions: [
      "Time spent performing compulsive behaviors",
      "Interference due to compulsive behaviors",
      "Distress associated with compulsive behaviors",
      "Resistance against compulsions",
      "Degree of control over compulsive behaviors",
    ],
  };

  const renderOptions = (section, qIndex) => {
    const labels = ["None", "Mild", "Moderate", "Severe", "Extreme"];
    if (qIndex === 4) {
      return [
        "Complete Control",
        "Much Control",
        "Moderate Control",
        "Little Control",
        "No Control",
      ].map((label, i) => (
        <label
          key={i}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            background: "#f9fafb",
            padding: "6px 10px",
            borderRadius: "8px",
            border: "1px solid #ddd",
            cursor: "pointer",
          }}
        >
          <input
            type="radio"
            name={`${section}-${qIndex}`}
            checked={
              (section === "obsession"
                ? obsessionScores[qIndex]
                : compulsionScores[qIndex]) === i
            }
            onChange={() => handleChange(section, qIndex, i)}
          />
          <span style={{ fontSize: "14px" }}>{i}. {label}</span>
        </label>
      ));
    }
    return labels.map((label, i) => (
      <label
        key={i}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          background: "#f9fafb",
          padding: "6px 10px",
          borderRadius: "8px",
          border: "1px solid #ddd",
          cursor: "pointer",
        }}
      >
        <input
          type="radio"
          name={`${section}-${qIndex}`}
          checked={
            (section === "obsession"
              ? obsessionScores[qIndex]
              : compulsionScores[qIndex]) === i
          }
          onChange={() => handleChange(section, qIndex, i)}
        />
        <span style={{ fontSize: "14px" }}>{label}</span>
      </label>
    ));
  };

  return (
    <div
      style={{
        background: "white",
        color: "#1f2937",
        maxWidth: "800px",
        margin: "20px auto",
        padding: "24px",
        borderRadius: "16px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
      }}
    >
      <h2
        style={{
          fontSize: "24px",
          fontWeight: "bold",
          textAlign: "center",
          color: "#1d4ed8",
        }}
      >
        CY-BOCS Severity Rating
      </h2>
      <h3
        style={{
          fontSize: "14px",
          textAlign: "center",
          color: "#6b7280",
          marginBottom: "20px",
        }}
      >
        Children’s Yale-Brown Obsessive Compulsive Scale
      </h3>

      {/* Obsessions Section */}
      <section>
        <h3
          style={{
            fontSize: "18px",
            fontWeight: "600",
            color: "#111827",
            borderBottom: "2px solid #e5e7eb",
            paddingBottom: "8px",
            marginBottom: "16px",
          }}
        >
          Obsessions (Items 1–5)
        </h3>
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {questions.obsessions.map((q, i) => (
            <div
              key={i}
              style={{
                padding: "16px",
                background: "#f9fafb",
                borderRadius: "12px",
                border: "1px solid #e5e7eb",
              }}
            >
              <h4
                style={{ fontWeight: "500", marginBottom: "10px", fontSize: "15px" }}
              >
                {i + 1}. {q}
              </h4>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                  gap: "10px",
                }}
              >
                {renderOptions("obsession", i)}
              </div>
            </div>
          ))}
        </div>
        <p
          style={{
            marginTop: "12px",
            fontWeight: "600",
            textAlign: "right",
            color: "#1d4ed8",
          }}
        >
          Subtotal (Obsessions): {subtotalObsessions}
        </p>
      </section>

      {/* Compulsions Section */}
      <section style={{ marginTop: "24px" }}>
        <h3
          style={{
            fontSize: "18px",
            fontWeight: "600",
            color: "#111827",
            borderBottom: "2px solid #e5e7eb",
            paddingBottom: "8px",
            marginBottom: "16px",
          }}
        >
          Compulsions (Items 6–10)
        </h3>
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {questions.compulsions.map((q, i) => (
            <div
              key={i}
              style={{
                padding: "16px",
                background: "#f9fafb",
                borderRadius: "12px",
                border: "1px solid #e5e7eb",
              }}
            >
              <h4
                style={{ fontWeight: "500", marginBottom: "10px", fontSize: "15px" }}
              >
                {i + 6}. {q}
              </h4>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                  gap: "10px",
                }}
              >
                {renderOptions("compulsion", i)}
              </div>
            </div>
          ))}
        </div>
        <p
          style={{
            marginTop: "12px",
            fontWeight: "600",
            textAlign: "right",
            color: "#1d4ed8",
          }}
        >
          Subtotal (Compulsions): {subtotalCompulsions}
        </p>
      </section>

      {/* Score + Classification */}
      <div
        style={{
          marginTop: "24px",
          padding: "20px",
          background: "#eff6ff",
          border: "1px solid #bfdbfe",
          borderRadius: "12px",
          textAlign: "center",
        }}
      >
        <p style={{ fontSize: "18px", fontWeight: "700", color: "#1e3a8a" }}>
          CY-BOCS Score: {score}
        </p>
        <p style={{ fontSize: "15px", marginTop: "6px", color: "#374151" }}>
          Classification:{" "}
          <span style={{ fontWeight: "600", color: "#2563eb" }}>{classify()}</span>
        </p>
      </div>

      
      <button
  onClick={handleSubmit}
  style={{
    marginTop: "20px",
    padding: "12px 25px",
    backgroundColor: "#1976d2",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: "bold",
    cursor: "pointer",
    transition: "background 0.3s ease",
  }}
  onMouseOver={(e) => (e.target.style.backgroundColor = "#115293")}
  onMouseOut={(e) => (e.target.style.backgroundColor = "#1976d2")}
>
  Submit
</button>

    </div>
  );
};

export default CyBocsRating;
